import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { MainLayout } from '@/layouts/MainLayout';
import FixtureDetailsView from '../components/fixtures/FixtureDetailsView';
import { FixtureDetailsSkeleton } from '../components/fixtures/FixtureDetailsSkeleton';
import { FixturesService } from '@/services/fixturesService';
import { RecentFixturesService } from '@/services/recentFixturesService';
import type { Fixture, RecentFixture } from '@/types/fixtures';

export default function FixtureDetailsPage() {
  const { fixtureId } = useParams<{ fixtureId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [fixture, setFixture] = useState<Fixture | RecentFixture | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detect if this is a recent fixtures route
  const isRecentFixture = location.pathname.includes('/recent-fixtures/');
  const backPath = isRecentFixture ? '/recent-fixtures' : '/fixtures';

  // Fetch single fixture by ID on mount
  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const fetchFixture = async () => {
      if (!fixtureId) {
        navigate(backPath);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const id = parseInt(fixtureId, 10);
        const data = isRecentFixture
          ? await RecentFixturesService.getRecentFixtureById(id)
          : await FixturesService.getFixtureById(id);

        // Only update state if component is still mounted
        if (isMounted && !abortController.signal.aborted) {
          setFixture(data);
        }
      } catch (err) {
        // Ignore aborted requests
        if (abortController.signal.aborted) {
          return;
        }

        if (isMounted) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to fetch fixture details';
          setError(errorMessage);
          console.error('Error fetching fixture details:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchFixture();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixtureId, isRecentFixture]);

  const handleGoBack = () => {
    if (!isRecentFixture && fixture) {
      navigate(backPath, { state: { fixtureId: fixture.fixtureId } });
      return;
    }

    navigate(backPath);
  };

  // Show loading state while fetching
  if (loading) {
    return (
      <MainLayout>
        <div className="space-y-4">
          {/* Back Navigation Skeleton */}
          <div className="px-4 md:px-0">
            <div className="h-9 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          </div>

          {/* Skeleton Content */}
          <FixtureDetailsSkeleton />
        </div>
      </MainLayout>
    );
  }

  // Show error state
  if (error || (!fixture && !loading)) {
    return (
      <MainLayout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGoBack}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back to Fixtures
            </Button>
          </div>

          {/* Error State */}
          <Alert variant="error">
            <div>
              <p className="font-medium">{error ? 'Error loading fixture' : 'Fixture not found'}</p>
              <p className="text-sm opacity-90">
                {error ||
                  'The requested fixture could not be found. It may have been removed or the link is invalid.'}
              </p>
            </div>
          </Alert>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Back Navigation */}
        <div className="px-4 md:px-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleGoBack}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to {isRecentFixture ? 'Recent Fixtures' : 'Fixtures'}
          </Button>
        </div>

        {/* Fixture Details */}
        {fixture && (
          <FixtureDetailsView fixture={fixture as Fixture} isRecentFixture={isRecentFixture} />
        )}
      </div>
    </MainLayout>
  );
}
